"use client";

import {
  AnimatePresence,
  motion,
  useAnimationControls,
  useMotionValue,
  useReducedMotion,
} from "motion/react";
import { useEffect, useRef, useState } from "react";
import type { IconType } from "react-icons";
import {
  LuChartBarBig as LuBarChartHorizontalBig,
  LuImage,
  LuMic,
  LuMusic,
  LuPencil,
  LuPlus,
  LuVideo,
} from "react-icons/lu";
import { useLocalStorage } from "usehooks-ts";

import type { AddPostAutoAction } from "@/components/app/app-layout/add-post-dialog-content";
import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import {
  focusIOSKeyboardBridge,
  isIOSDocument,
} from "@/components/app/editor-v3/editor-dom-focus";
import { Button } from "@/components/ui/button";
import { useIsClient } from "@/hooks/use-is-client";
import { cn } from "@/utils/lib";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

enum Position {
  Left,
  Right,
}

const FAB_ACTIONS: {
  label: string;
  icon: IconType;
  autoAction: AddPostAutoAction | null;
}[] = [
  { label: "Post", icon: LuPencil, autoAction: null },
  { label: "Photo", icon: LuImage, autoAction: "photo" },
  { label: "Video", icon: LuVideo, autoAction: "video" },
  { label: "Audio", icon: LuMusic, autoAction: "audio" },
  { label: "Record", icon: LuMic, autoAction: "record" },
  { label: "Poll", icon: LuBarChartHorizontalBig, autoAction: "poll" },
];

const FAB_BACKDROP_VERTICAL_MASK =
  "linear-gradient(180deg, #00000000 0%, #000000FF 14%, #000000FF 86%, #00000000 100%)";

const FAB_BACKDROP_HORIZONTAL_MASK =
  "linear-gradient(90deg, #00000000 0%, #000000FF 14%, #000000FF 86%, #00000000 100%)";

export function AddPostFloatingButton() {
  const buttonRef = useRef<HTMLDivElement>(null!);
  const controls = useAnimationControls();
  const [buttonPosition, saveButtonPosition] = useLocalStorage<Position>(
    LocalStorageKeys.floatingPostButton,
    Position.Right,
  );
  const clickEnabledRef = useRef(true);
  const scrollTopRef = useRef(0);
  const isClient = useIsClient();
  const prefersReducedMotion = useReducedMotion();
  const [menuOpen, setMenuOpen] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const isRightAligned = buttonPosition === Position.Right;
  const itemDirection = isRightAligned ? "flex-row" : "flex-row-reverse";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const lastScrollTop = scrollTopRef.current;

      if (currentScrollPos > lastScrollTop) {
        controls.start({
          opacity: 0.4,
          transition: { type: "spring", duration: 1 },
        });
      } else {
        controls.start({
          opacity: 1,
          transition: { type: "spring", duration: 1 },
        });
      }
      scrollTopRef.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [controls]);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!buttonRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [menuOpen]);

  const handleDragEnd = () => {
    const { x, width } = buttonRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;

    const newPosition =
      x + width / 2 > windowWidth / 2 ? Position.Right : Position.Left;
    controls.start({
      x: 0,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 },
    });
    saveButtonPosition(newPosition);
  };

  const handleMenuAction = (nextAutoAction: AddPostAutoAction | null) => {
    if (!nextAutoAction && isIOSDocument()) {
      focusIOSKeyboardBridge();
    }

    setMenuOpen(false);
    setDialog(DIALOG.AddPost, {
      open: true,
      props: nextAutoAction
        ? {
            autoAction: nextAutoAction,
          }
        : undefined,
    });
  };

  if (!isClient) {
    return null;
  }

  return (
    <motion.div
      ref={buttonRef}
      className={cn(
        `
          fixed bottom-(--safe-screen-offset-bottom) z-100 mb-4 block
          cursor-move touch-none opacity-0
          lg:hidden
        `,
        {
          "right-4": buttonPosition === Position.Right,
          "left-4": buttonPosition === Position.Left,
        },
      )}
      drag={!menuOpen}
      dragElastic={0}
      dragMomentum={false}
      onDrag={() => {
        clickEnabledRef.current = false;
        setMenuOpen(false);
        controls.start({
          opacity: 1,
          transition: { type: "spring", duration: 1 },
        });
      }}
      onTouchStart={() => {
        controls.start({
          opacity: 1,
          transition: { type: "spring", duration: 1 },
        });
      }}
      onDragEnd={handleDragEnd}
      style={{ x, y }}
      animate={controls}
    >
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className={cn("absolute bottom-full mb-3 flex flex-col gap-2", {
              "right-0 items-end": isRightAligned,
              "left-0 items-start": !isRightAligned,
            })}
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              open: {
                transition: prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      staggerChildren: 0.05,
                      delayChildren: 0.02,
                      staggerDirection: -1,
                    },
              },
              closed: {
                transition: prefersReducedMotion
                  ? { duration: 0 }
                  : { staggerChildren: 0.04 },
              },
            }}
          >
            <motion.div
              className="
                pointer-events-none absolute -inset-4 -z-10
              "
              style={{
                transformOrigin: isRightAligned
                  ? "bottom right"
                  : "bottom left",
                willChange: prefersReducedMotion
                  ? undefined
                  : "transform, opacity",
              }}
              variants={{
                open: prefersReducedMotion
                  ? {
                      opacity: 1,
                    }
                  : {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.24,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    },
                closed: prefersReducedMotion
                  ? {
                      opacity: 0,
                    }
                  : {
                      opacity: 0,
                      y: 8,
                      transition: {
                        duration: 0.18,
                        ease: [0.4, 0, 1, 1],
                      },
                    },
              }}
            >
              <div
                className="absolute inset-0 rounded-4xl"
                style={{
                  WebkitMaskImage: FAB_BACKDROP_VERTICAL_MASK,
                  maskImage: FAB_BACKDROP_VERTICAL_MASK,
                }}
              >
                <div
                  className="
                    absolute inset-0 rounded-4xl
                    bg-[color-mix(in_srgb,var(--background),transparent_74%)]
                    shadow-[0_22px_56px_rgba(0,0,0,0.42)] backdrop-blur-xl
                  "
                  style={{
                    WebkitMaskImage: FAB_BACKDROP_HORIZONTAL_MASK,
                    maskImage: FAB_BACKDROP_HORIZONTAL_MASK,
                  }}
                />
              </div>
            </motion.div>
            {FAB_ACTIONS.map(
              ({ label, icon: Icon, autoAction: nextAutoAction }) => (
                <motion.div
                  key={label}
                  className={cn("flex items-center gap-2", itemDirection)}
                  variants={{
                    open: prefersReducedMotion
                      ? { opacity: 1, scale: 1, y: 0 }
                      : {
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          transition: {
                            type: "spring",
                            stiffness: 420,
                            damping: 28,
                            mass: 0.6,
                          },
                        },
                    closed: prefersReducedMotion
                      ? { opacity: 0, scale: 1, y: 0 }
                      : {
                          opacity: 0,
                          scale: 0.92,
                          y: 14,
                          transition: {
                            duration: 0.18,
                            ease: [0.4, 0, 1, 1],
                          },
                        },
                  }}
                >
                  <div
                    className="
                      rounded-full border bg-background/95 px-3 py-1.5 text-sm
                      font-medium text-foreground
                      shadow-[0_14px_32px_rgba(0,0,0,0.38)]
                    "
                  >
                    {label}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      `
                        unforce-radius size-11 rounded-full bg-background/95
                        shadow-[0_14px_32px_rgba(0,0,0,0.38)]
                      `,
                    )}
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      clickEnabledRef.current = true;
                      handleMenuAction(nextAutoAction);
                    }}
                  >
                    <Icon className="text-lg" />
                  </Button>
                </motion.div>
              ),
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        className="unforce-radius size-12 rounded-full p-0 text-lg shadow-lg"
        onClick={(ev) => {
          if (!clickEnabledRef.current) {
            ev.preventDefault();
            ev.stopPropagation();
            clickEnabledRef.current = true;
            return;
          }

          setMenuOpen((open) => !open);
          controls.start({
            opacity: 1,
            transition: { type: "spring", duration: 1 },
          });
        }}
      >
        <motion.div
          animate={{ rotate: menuOpen ? 45 : 0 }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }
          }
        >
          <LuPlus />
        </motion.div>
      </Button>
    </motion.div>
  );
}
